import React, { useState, useEffect } from 'react';
import { X, UserCog, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import {
  FamilyMember,
  HomeRole,
  ServiceType,
  ALL_SERVICE_TYPES,
  SERVICE_TYPE_LABELS,
  updateMemberAccess,
} from '../../services/family.api';

interface EditMemberSheetProps {
  member: FamilyMember;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ROLES: { value: HomeRole; label: string; desc: string }[] = [
  { value: 'member', label: 'Member', desc: 'Can view and manage assigned assets & services' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access to assigned assets' },
];

export function EditMemberSheet({ member, open, onClose, onSuccess }: EditMemberSheetProps) {
  const [role, setRole]               = useState<HomeRole>(member.role ?? 'member');
  const [relation, setRelation]       = useState(member.relation ?? '');
  const [grantFuture, setGrantFuture] = useState(member.grantFutureAssets ?? false);
  const [services, setServices]       = useState<ServiceType[]>(
    member.serviceAccess?.map((a) => a.serviceType) ?? [...ALL_SERVICE_TYPES]
  );
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // Reset form whenever the sheet opens for a (possibly different) member
  useEffect(() => {
    if (open) {
      setRole(member.role ?? 'member');
      setRelation(member.relation ?? '');
      setGrantFuture(member.grantFutureAssets ?? false);
      setServices(member.serviceAccess?.map((a) => a.serviceType) ?? [...ALL_SERVICE_TYPES]);
      setError('');
    }
  }, [open, member]);

  if (!open) return null;

  const toggleService = (s: ServiceType) =>
    setServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateMemberAccess(member.id, {
        role,
        relation: relation.trim() || undefined,
        grantFutureAssets: grantFuture,
        serviceTypes: services,
      });
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const displayName = member.user?.name ?? member.user?.email ?? 'Member';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 sm:inset-auto sm:right-0 sm:top-0 sm:bottom-0 sm:w-[440px] flex flex-col bg-background border-l shadow-2xl rounded-t-2xl sm:rounded-none">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-foreground/5 flex items-center justify-center">
              <UserCog className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold leading-none">Edit Access</h2>
              <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[220px]">{displayName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5 flex-1">

            {/* Member info (read-only) */}
            {member.user?.email && (
              <div className="px-3.5 py-3 rounded-xl bg-secondary/50 border">
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium mt-0.5">{member.user.email}</p>
              </div>
            )}

            {/* Relation */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Relation <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={relation}
                onChange={(e) => setRelation(e.target.value)}
                placeholder="e.g. Spouse, Child, Roommate"
                className="w-full h-9 px-3 text-sm rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-foreground/20 placeholder:text-muted-foreground"
              />
            </div>

            {/* Role */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Role</label>
              <div className="space-y-2">
                {ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={cn(
                      'w-full flex items-start gap-3 px-3.5 py-3 rounded-xl border text-left transition-all',
                      role === r.value
                        ? 'border-foreground bg-foreground/5'
                        : 'border-border hover:border-foreground/30'
                    )}
                  >
                    <div className={cn(
                      'mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                      role === r.value ? 'border-foreground bg-foreground' : 'border-muted-foreground'
                    )}>
                      {role === r.value && <div className="h-1.5 w-1.5 rounded-full bg-background" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{r.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Service access */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Service access</label>
              <p className="text-xs text-muted-foreground">Choose which services this person can use</p>
              <div className="grid grid-cols-2 gap-2 pt-1">
                {ALL_SERVICE_TYPES.map((s) => {
                  const checked = services.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleService(s)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all text-left',
                        checked
                          ? 'border-foreground bg-foreground/5 text-foreground'
                          : 'border-border text-muted-foreground hover:border-foreground/30'
                      )}
                    >
                      <div className={cn(
                        'h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                        checked ? 'border-foreground bg-foreground' : 'border-muted-foreground'
                      )}>
                        {checked && <Check className="h-2.5 w-2.5 text-background" />}
                      </div>
                      {SERVICE_TYPE_LABELS[s]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Grant future assets */}
            <div className="flex items-start gap-3 px-3.5 py-3 rounded-xl border">
              <button
                type="button"
                onClick={() => setGrantFuture((v) => !v)}
                className={cn(
                  'mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                  grantFuture ? 'border-foreground bg-foreground' : 'border-muted-foreground'
                )}
              >
                {grantFuture && <Check className="h-2.5 w-2.5 text-background" />}
              </button>
              <div>
                <p className="text-sm font-medium">Auto-share future assets</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Automatically grant access to assets added to this home in the future
                </p>
              </div>
            </div>

            {error && (
              <p className="text-xs text-destructive px-1">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex gap-3 shrink-0">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1 gap-1.5" disabled={saving}>
              {saving ? (
                <span className="h-3.5 w-3.5 border-2 border-background/40 border-t-background rounded-full animate-spin" />
              ) : (
                <UserCog className="h-3.5 w-3.5" />
              )}
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
